import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import SetPasswordFields from "@/components/tpr/SetPasswordFields/SetPasswordFields";

interface VerifyAndSetStepProps {
  email: string;
  onVerify: (code: string) => Promise<boolean>;
  onSetPassword: (code: string, password: string) => void;
  onBack: () => void;
  pending: boolean;
  error?: string;
}

const VerifyAndSetStep = ({ email, onVerify, onSetPassword, onBack, pending, error }: VerifyAndSetStepProps) => {
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const masked = email.replace(/^(.).*(@.*)$/, "$1…$2");
  const canSet = verified && password.length >= 8 && password === confirm;

  const handleVerify = async () => { if (await onVerify(code)) setVerified(true); };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">We&apos;ve emailed a 6-digit code to {masked}.</p>
      {error && (
        <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800" role="alert">{error}</div>
      )}

      {!verified ? (
        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="otp">Verification code</FieldLabel>
                <Input id="otp" inputMode="numeric" maxLength={6} className="bg-white dark:text-gray-900 text-lg tracking-widest" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                {import.meta.env.DEV && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Dev: use code 888888 to skip the email.</p>
                )}
              </Field>
              <Field orientation="horizontal">
                <Button type="submit" className="w-full" disabled={code.length !== 6 || pending} aria-label="Verify code">Verify code</Button>
              </Field>
            </FieldGroup>
          </FieldSet>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); onSetPassword(code, password); }}>
          <SetPasswordFields password={password} confirm={confirm} onPasswordChange={setPassword} onConfirmChange={setConfirm} />
          <Button type="submit" className="w-full mt-4" disabled={!canSet || pending} aria-label="Set password and continue">Set password &amp; continue</Button>
        </form>
      )}

      <button type="button" onClick={onBack} className="text-sm text-gray-500 underline self-start">Use a different email</button>
    </div>
  );
};

export default VerifyAndSetStep;
